import { NavLink, Route, Routes, Navigate } from 'react-router-dom';
import { SignedIn, SignedOut, SignIn, UserButton } from '@clerk/clerk-react';
import OverviewPanel from '@/components/OverviewPanel';
import PipelineVisualization from '@/components/PipelineVisualization';
import BatchJobsTable from '@/components/BatchJobsTable';
import BatchDetail from '@/components/BatchDetail';
import { FailingMessagesPanel } from '@/components/FailingMessagesPanel';
import { ReindexJobList } from '@/components/reindex/ReindexJobList';
import { ReindexJobNew } from '@/components/reindex/ReindexJobNew';
import { ReindexJobDetail } from '@/components/reindex/ReindexJobDetail';
import { Button } from '@/components/ui/button';
import {
  LayoutDashboard,
  ListFilter,
  AlertCircle,
  RotateCw,
} from 'lucide-react';

function navLinkClass({ isActive }: { isActive: boolean }) {
  return isActive
    ? 'inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground'
    : 'inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium hover:bg-accent hover:text-accent-foreground';
}

function AuthenticatedApp() {
  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto flex h-16 items-center px-4">
          <div className="mr-4 flex">
            <NavLink to="/" className="mr-6 flex items-center space-x-2">
              <LayoutDashboard className="h-6 w-6" />
              <span className="hidden font-bold sm:inline-block">
                Entity Indexing Dashboard
              </span>
            </NavLink>
          </div>
          <nav className="flex flex-1 items-center justify-end space-x-2">
            <NavLink to="/" end className={navLinkClass}>
              <LayoutDashboard className="h-4 w-4" />
              <span className="hidden sm:inline">Overview</span>
            </NavLink>
            <NavLink to="/batches" className={navLinkClass}>
              <ListFilter className="h-4 w-4" />
              <span className="hidden sm:inline">Batch Jobs</span>
            </NavLink>
            <NavLink to="/errors" className={navLinkClass}>
              <AlertCircle className="h-4 w-4" />
              <span className="hidden sm:inline">Errors</span>
            </NavLink>
            <NavLink to="/reindex" className={navLinkClass}>
              <RotateCw className="h-4 w-4" />
              <span className="hidden sm:inline">Reindex</span>
            </NavLink>
            <UserButton afterSignOutUrl="/" />
          </nav>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6">
        <Routes>
          <Route
            path="/"
            element={
              <div className="space-y-6">
                <div>
                  <h2 className="text-3xl font-bold tracking-tight">Overview</h2>
                  <p className="text-muted-foreground">
                    Monitor your entity indexing pipeline in real-time
                  </p>
                </div>
                <OverviewPanel />
                <PipelineVisualization />
              </div>
            }
          />
          <Route
            path="/batches"
            element={
              <div className="space-y-6">
                <div>
                  <h2 className="text-3xl font-bold tracking-tight">Batch Jobs</h2>
                  <p className="text-muted-foreground">
                    View and filter all AI batch processing jobs
                  </p>
                </div>
                <BatchJobsTable />
              </div>
            }
          />
          <Route path="/batches/:id" element={<BatchDetail />} />
          <Route
            path="/errors"
            element={
              <div className="space-y-6">
                <div>
                  <h2 className="text-3xl font-bold tracking-tight">
                    Failing Messages & Errors
                  </h2>
                  <p className="text-muted-foreground">
                    Monitor documents with errors, retry counts, and failure details
                  </p>
                </div>
                <FailingMessagesPanel />
              </div>
            }
          />
          <Route path="/reindex" element={<ReindexJobList />} />
          <Route path="/reindex/new" element={<ReindexJobNew />} />
          <Route path="/reindex/:id" element={<ReindexJobDetail />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

function SignInScreen() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="flex flex-col items-center space-y-6">
        <h1 className="text-2xl font-bold">Entity Indexing Dashboard</h1>
        <p className="text-sm text-muted-foreground">
          Sign in with your @anthoscapital.com Google account to continue.
        </p>
        <SignIn routing="hash" />
        <noscript>
          <Button variant="ghost">Please enable JavaScript</Button>
        </noscript>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <>
      <SignedIn>
        <AuthenticatedApp />
      </SignedIn>
      <SignedOut>
        <SignInScreen />
      </SignedOut>
    </>
  );
}
